import { useConnectForm } from "@/contexts/ConnectFormContext";
import { useDarkTheme } from "@/contexts/DarkTheme";
import { useSidebarContext } from "@/contexts/SidebarContext";
import { generateTokenForSocket, useIsMobile } from "@/helpers/helperFunctions";
import { cn, SUPPORT_MODAL_TITLE } from "@/lib/utils";
import { useMediaQuery } from "@react-hook/media-query";
import { ChartNoAxesGantt, Gauge, Rocket, Unplug } from "lucide-react";
import { signOut } from "next-auth/react";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { BsBarChart } from "react-icons/bs";
import { IoCreateOutline } from "react-icons/io5";
import io from "socket.io-client";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
// const socketSecret = process.env.NEXT_PUBLIC_SOCKET_SECRET;
import React from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "../ui/menubar";
import RiskWarnings from "./riskWarnings";
import WhatsNew from "./whatsNew";
import {
  brokerConnectModalAtom,
  technicalSupportModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import { useRecoilState } from "recoil";
import { enableVideoTutorial } from "@/config/logos";

const SideBar = (props: { showDisabled?: boolean }) => {
  const { t } = useTranslation("layout");
  const { pathname, query, push, reload } = useRouter();
  const { showSidebar, setShowSidebar } = useSidebarContext();
  const { darkTheme } = useDarkTheme();
  const matchesMobile = useMediaQuery(`only screen and (max-width: 640px)`);
  const [show, setShow] = useState<boolean>(false);
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom
  );
  const [showTechnicalRequest, setShowTechnicalRequest] = useRecoilState(
    technicalSupportModalAtom
  );
  const isMobile = useIsMobile(false);
  // Popup logic start
  const [showPopup, setShowPopup] = useState(false);
  useEffect(() => {
    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith("changelog_v1="))
      ?.split("=")[1];

    if (cookieValue !== "1") {
      // commented because the updates are outdated now. Comment to true to revive this
      setShowPopup(false);
    }
  }, []);
  const handleClose = () => {
    setShowPopup(false);
    document.cookie = `changelog_v1=1; max-age=${30 * 24 * 60 * 60}; path=/`;
  };
  // popup logic closed

  useEffect(() => {
    document.body.style.overflow =
      showSidebar && matchesMobile ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto"; // Cleanup
    };
  }, [showSidebar, matchesMobile]);

  const Logout = () => {
    let u = JSON.parse(localStorage.getItem("user-info")!);
    const socket = io("https://wsstest.trylevel2.com:3222", {
      auth: {
        secret: generateTokenForSocket,
      },
    });
    socket.emit("addUserToRoom", "web.canvas.attendance");
    socket.emit("web.canvas.attendance", {
      msg: {
        first_name: u?.first_name,
        last_name: u?.last_name,
        strategy: "",
        key: u?.key,
        user_id: u?.user_id,
        status: "unsubscribed",
      },
      room: "web.canvas.attendance",
    });
    localStorage.clear();
    signOut({ redirect: false });
    push({ pathname: "/logout", query: query }).then(() => reload());
    return false;
  };

  // Close Sidebar for Mobile
  let closeSidebarMobile = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (isMobile) {
      setShowSidebar(!showSidebar);
    }
  };
  return (
    <>
      <Dialog open={show} onOpenChange={() => setShow(false)}>
        <DialogContent className="max-w-4xl">
          <RiskWarnings hideSeprator />
        </DialogContent>
      </Dialog>
      <Dialog open={showPopup} onOpenChange={handleClose}>
        <DialogContent className="max-w-xl">
          <WhatsNew />
        </DialogContent>
      </Dialog>

      <nav
        className={cn(
          "overflow-hidden px-3.5 lg:block !h-[full]   transition-all duration-300 ease-in-out ",
          matchesMobile ? "fixed left-0 z-20 lg:z-20 " : "",
          showSidebar
            ? "min-w-[200px] max-w-[252px] w-full  opacity-100"
            : "min-w-[70px]  ",
          matchesMobile && showSidebar
            ? ""
            : !matchesMobile
            ? ""
            : "opacity-0 ",
          darkTheme ? "bg-[#081121] " : "bg-white",
          darkTheme ? "shadow-md" : "",
          props?.showDisabled
            ? "hidden md:block md:pointer-events-none md:opacity-50"
            : ""
        )}
      >
        <div
          className={`flex flex-col justify-between relative sm:fixed h-[calc(100vh-135px)] lg:h-[calc(100vh-140px)]  xl:h-[calc(100vh-105px)] `}
        >
          <ul className="flex flex-col gap-[8px] cursor-pointer w-full mt-5">
            <Link
              href={`/build?locale=${query.locale || "en"}`}
              onClick={closeSidebarMobile}
            >
              <HoverCard openDelay={0}>
                <HoverCardTrigger>
                  <li
                    className={cn(
                      "flex gap-5 items-center  text-sm duration-300 rounded-[6px] p-2 capitalize hover:bg-primary text-foreground hover:text-primary-foreground",
                      pathname?.includes("build")
                        ? "bg-primary text-primary-foreground"
                        : "",
                      showSidebar ? "" : "justify-center"
                    )}
                  >
                    <IoCreateOutline size={15} />

                    {!showSidebar ? (
                      <HoverCardContent
                        className={cn(
                          "w-auto ml-[52px] mt-[-20px] p-2 pl-3 pr-3 font-medium "
                        )}
                      >
                        {t("Create")}
                      </HoverCardContent>
                    ) : (
                      <span className={cn(showSidebar ? "" : "hidden")}>
                        {t("Create")}
                      </span>
                    )}
                  </li>
                </HoverCardTrigger>
              </HoverCard>
            </Link>
            <Link
              href={`/test?locale=${query.locale || "en"}`}
              onClick={closeSidebarMobile}
            >
              <HoverCard openDelay={0}>
                <HoverCardTrigger>
                  <li
                    className={cn(
                      "flex gap-5 items-center text-sm duration-300 rounded-[6px] p-2 capitalize hover:bg-primary text-foreground hover:text-primary-foreground",
                      // pathname?.includes("test")
                      pathname == "/test" ||
                        pathname?.includes("strategy_details")
                        ? "bg-primary text-primary-foreground"
                        : "",
                      showSidebar ? "" : "justify-center"
                    )}
                  >
                    <Gauge size={15} />
                    {/* <span className={cn(showSidebar ? "" : "hidden")}>{t("Test")}</span> */}

                    {!showSidebar ? (
                      <HoverCardContent
                        className={cn(
                          "w-auto ml-[52px] mt-[-20px] p-2 pl-3 pr-3 font-medium "
                        )}
                      >
                        {t("Test")}
                      </HoverCardContent>
                    ) : (
                      <span className={cn(showSidebar ? "" : "hidden")}>
                        {t("Test")}
                      </span>
                    )}
                  </li>
                </HoverCardTrigger>
              </HoverCard>
            </Link>
            <Link
              href={`/deploy?locale=${query.locale || "en"}`}
              onClick={closeSidebarMobile}
            >
              <HoverCard openDelay={0}>
                <HoverCardTrigger>
                  <li
                    className={cn(
                      "flex gap-5 items-center text-sm duration-300 rounded-[6px] p-2  capitalize hover:bg-primary text-foreground hover:text-primary-foreground",
                      pathname?.includes("deploy") ||
                        pathname?.includes("forward")
                        ? "bg-primary text-primary-foreground"
                        : "",
                      showSidebar ? "" : "justify-center"
                    )}
                  >
                    <Rocket size={15} />
                    {/* <span className={cn(showSidebar ? "" : "hidden")}>
                {t("Deploy")}
                </span> */}

                    {!showSidebar ? (
                      <HoverCardContent
                        className={cn(
                          "w-auto ml-[52px] mt-[-20px] p-2 pl-3 pr-3 font-medium "
                        )}
                      >
                        {t("Deploy")}
                      </HoverCardContent>
                    ) : (
                      <span className={cn(showSidebar ? "" : "hidden")}>
                        {t("Deploy")}
                      </span>
                    )}
                  </li>
                </HoverCardTrigger>
              </HoverCard>
            </Link>

            <HoverCard openDelay={0}>
              <HoverCardTrigger>
                <li
                  onClick={() => setShowConnectForm(true)}
                  className={cn(
                    "flex gap-5 items-center text-sm duration-300 rounded-[6px] p-2  capitalize hover:bg-primary text-foreground hover:text-primary-foreground",
                    showSidebar ? "" : "justify-center"
                  )}
                >
                  <Unplug size={15} />
                  {!showSidebar ? (
                    <HoverCardContent
                      className={cn(
                        "w-auto ml-[52px] mt-[-20px] p-2 pl-3 pr-3 font-medium "
                      )}
                    >
                      {t("Connect API")}
                    </HoverCardContent>
                  ) : (
                    <span className={cn(showSidebar ? "" : "hidden")}>
                      {t("Connect API")}
                    </span>
                  )}
                </li>
              </HoverCardTrigger>
            </HoverCard>
            <Link
              href={`/marketplace?locale=${query.locale || "en"}`}
              onClick={closeSidebarMobile}
            >
              <HoverCard openDelay={0}>
                <HoverCardTrigger>
                  <li
                    className={cn(
                      "flex gap-5 items-center text-sm duration-300 rounded-[6px] p-2  capitalize hover:bg-primary text-foreground hover:text-primary-foreground",
                      pathname?.includes("marketplace")
                        ? "bg-primary text-primary-foreground"
                        : "",
                      showSidebar ? "" : "justify-center"
                    )}
                  >
                    <BsBarChart size={15} />
                    {/* <span className={cn(showSidebar ? "" : "hidden")}>
                {t("Deploy")}
                </span> */}

                    {!showSidebar ? (
                      <HoverCardContent
                        className={cn(
                          "w-auto ml-[52px] mt-[-20px] p-2 pl-3 pr-3 font-medium "
                        )}
                      >
                        {t("Marketplace")}
                      </HoverCardContent>
                    ) : (
                      <span className={cn(showSidebar ? "" : "hidden")}>
                        {t("Marketplace")}
                      </span>
                    )}
                  </li>
                </HoverCardTrigger>
              </HoverCard>
            </Link>
            {/* data-[state=open]:bg-primary data-[state=open]:text-primary-foreground */}

            <Menubar className="flex flex-col h-fit border-none p-0  items-start w-full">
              <MenubarMenu>
                <MenubarTrigger
                  className={cn(
                    "flex w-full p-2  gap-5 items-center text-sm duration-300 rounded-[6px] py-[6px]  capitalize hover:bg-primary text-foreground hover:text-primary-foreground  focus:bg-transparent",
                    showSidebar ? "" : "justify-center"
                  )}
                >
                  <ChartNoAxesGantt size={15} />
                  <span className={cn(showSidebar ? "" : "hidden")}>
                    {t("More")}
                  </span>
                </MenubarTrigger>
                <MenubarContent>
                  <Link href="/test?tab=scanners" rel="noopener noreferrer">
                    <MenubarItem className="cursor-pointer">
                      {t("Scanners")}
                    </MenubarItem>
                  </Link>

                  {/* <Link href="/subscription">
                    <MenubarItem className="cursor-pointer">
                      {t("Manage Subscription")}
                    </MenubarItem>
                  </Link> */}

                  {/* <Link href="/broker-stats-v2">
                    <MenubarItem className="cursor-pointer">
                      {t("Broker Dashboard")}
                    </MenubarItem>
                  </Link> */}
                  {enableVideoTutorial && (
                    <Link
                      href="https://www.youtube.com/@trylevel2/videos"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <MenubarItem className="cursor-pointer">
                        {t("Explainer Videos")}
                      </MenubarItem>
                    </Link>
                  )}

                  <MenubarItem
                    onClick={() => {
                      setShowTechnicalRequest({
                        isOpen: true,
                        isRequest: false,
                        helpline: true,
                      });
                    }}
                    className="cursor-pointer"
                  >
                    {t("Technical support")}
                  </MenubarItem>
                  <MenubarItem className="cursor-pointer" onClick={Logout}>
                    {t("Logout")}
                  </MenubarItem>
                </MenubarContent>
              </MenubarMenu>
            </Menubar>
          </ul>
          {showSidebar && (
            <div className="mb-3 flex flex-col gap-2 text-xs">
              {/* <a
                href="https://learn.trylevel2.com/docs/miscellaneous/risk-disclosure"
                target="_blank"
                className="underline cursor-pointer w-fit p-0 text-muted-foreground text-xs"
              >
                {t("Risk disclosure")}
              </a> */}
              {/* <div className="text-muted-foreground text-xs">
                © {new Date()?.getFullYear()} Plus100
              </div> */}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default SideBar;
